import { useQueryClient } from '@tanstack/react-query';
import { useOptimisticMutation, type Variables } from '../../../hooks/use-optimistic-mutation';
import { ApiResponseError } from '../../../types/errors';
import type { PriceListUpsertError } from '../errors';
import { PriceListError } from '../errors';
import type { PriceList, PriceListApiResponse } from '../types';
import { sortLists } from '.';
import { QK_ALL_PRICE_LISTS, QK_PAGINATED_PRICE_LISTS } from './use-price-lists';
import { usePriceListsApi } from './use-price-lists-api';
import { useUploadCsv } from './use-upload-csv';

export function useCreatePriceList() {
  const queryClient = useQueryClient();
  const { mutateAsync: uploadCsv } = useUploadCsv();
  const { priceListsApi } = usePriceListsApi();

  async function createList({ priceList, manualCsv }: { priceList: PriceList; manualCsv?: File }) {
    try {
      await priceListsApi.create(priceList);
    } catch (err) {
      if (
        ApiResponseError.isApiResponseError(err, ApiResponseError.errorSchemas.detail) &&
        err.status === 400
      ) {
        if (err.data.detail === 'Price List already exists with slug') {
          throw new PriceListError('duplicate_slug', { slug: priceList.slug });
        }
      }

      throw new PriceListError('create_failed');
    }

    if (manualCsv) {
      try {
        await uploadCsv({ slug: priceList.slug, file: manualCsv });
      } catch (err) {
        // Delete the price list if the CSV was invalid (otherwise we have price list in
        // half-complete state)
        await priceListsApi.priceList(priceList.slug).delete();

        // Rethrow so we can show errors to user
        throw err;
      }
    }
  }

  return useOptimisticMutation<
    PriceListApiResponse[],
    Variables<typeof createList>,
    PriceListUpsertError
  >({
    queryClient,
    queryKey: QK_PAGINATED_PRICE_LISTS(),
    invalidateKeys: [QK_ALL_PRICE_LISTS()],
    mutationFn: createList,
    // Sorted by name to match backend
    optimisticUpdate: (prev, data) => {
      return sortLists([...(prev ?? []), { ...data.priceList, num_manual_prices: 0 }]);
    },
  });
}
